import ButtonComponent from 'components/ButtonComponent';
import { updateUserParams } from 'lib/communication/userInfo';
import { Param } from 'lib/types';
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import permissionService, { DEF_WINDOW, NEW_ORDER_VIEW, NEW_ORDER_VIEW_MOBILE, ORD_PAGE_COUNT } from 'services';
import './Settings.scss';
import { useSelector } from 'react-redux';
import { AppState, useUserParam } from 'reducers';
import { DefaultOrdersViewSettingWrapper, DefaultViewSettingWrapper, NumberOfOrdersPerPageSettingWrapper } from 'components/SettingsWrappers';
import DownloadManual from 'components/DownloadManual';
import { useDevice } from 'lib/hooks/useDevice';

const Settings = () => {
    const [size, setSize] = useState<number>(10);
    const [defaultView, setDefaultView] = useState<string>('Akt');
    const [defaultDesktopView, setDefaultDesktopView] = useState<string>('');
    const [defaultMobileView, setDefaultMobileView] = useState<string>('');

    const isPlateCreatorOn = useSelector((state: AppState) => state.conf.isPlateCreatorOn);

    const [messageSize, setMessageSize] = useState<string>('');
    const [messageDefaultView, setMessageDefaultView] = useState<string>('');
    const [messageDefaultDesktopView, setMessageDefaultDesktopView] = useState<string>('');
    const [messageDefaultMobileView, setMessageDefaultMobileViwe] = useState<string>('');

    const [isSizeFetching, setIsSizeFetching] = useState<boolean>(false);
    const [isDefaultViewFetching, setIsDefaultViewFetching] = useState<boolean>(false);
    const [isDefaultDesktopViewFetching, setIsDesfaultDesktopViewFetching] = useState<boolean>(false);
    const [isDefaultMobileViewFetching, setIsDefasultMobileViewFetching] = useState<boolean>(false);

    const { isDesktop } = useDevice();

    const defWindowParam = useUserParam(DEF_WINDOW);
    const pageCountParam = useUserParam(ORD_PAGE_COUNT);
    const newOrderViewParam = useUserParam(NEW_ORDER_VIEW);
    const newOrderViewMobileParam = useUserParam(NEW_ORDER_VIEW_MOBILE);

    useEffect(() => {
        setSize(parseInt(pageCountParam ?? '10'));
        setDefaultView(defWindowParam ?? 'Akt');
        setDefaultDesktopView(newOrderViewParam ?? 'basic');
        setDefaultMobileView(newOrderViewMobileParam ?? 'basic');
    }, []);

    const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSize(parseInt(event.currentTarget.value));
    };

    const handleDefaultViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultView(event.currentTarget.value);
    };

    const handleDesktopViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultDesktopView(event.currentTarget.value);
    };

    const handleMobileViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultMobileView(event.currentTarget.value);
    };

    const handleSaveSizeChange = async () => {
        setIsSizeFetching(true);
        const updateParamPageCount: Param = {
            ParamName: ORD_PAGE_COUNT,
            ParamValue: size.toString()
        };

        const params = await updateUserParams(updateParamPageCount);
        setMessageSize(params.ok ? 'Zapisano' : 'Błąd');
        setIsSizeFetching(false);
    };

    const handleSaveDefaultViewChange = async () => {
        setIsDefaultViewFetching(true);
        const updateParamDefaultView: Param = {
            ParamName: DEF_WINDOW,
            ParamValue: defaultView
        };

        const params = await updateUserParams(updateParamDefaultView);
        setMessageDefaultView(params.ok ? 'Zapisano' : 'Błąd');
        setIsDefaultViewFetching(false);
    };

    const handleSaveDesktopViewChange = async () => {
        setIsDesfaultDesktopViewFetching(true);
        const updateParamDesktopView: Param = {
            ParamName: NEW_ORDER_VIEW,
            ParamValue: defaultDesktopView
        };

        const params = await updateUserParams(updateParamDesktopView);
        setMessageDefaultDesktopView(params.ok ? 'Zapisano' : 'Błąd');
        setIsDesfaultDesktopViewFetching(false);
    };

    const handleSaveMobileViewChange = async () => {
        setIsDefasultMobileViewFetching(true);
        const updateParamMobileView: Param = {
            ParamName: NEW_ORDER_VIEW_MOBILE,
            ParamValue: defaultMobileView
        };

        const params = await updateUserParams(updateParamMobileView);
        setMessageDefaultMobileViwe(params.ok ? 'Zapisano' : 'Błąd');
        setIsDefasultMobileViewFetching(false);
    };

    return (
        <div className='Settings'>
            <DownloadManual />
            {permissionService.haveAccessToOperation('editDefaultView') &&
                <>
                    <h2>Widok domyślny</h2>
                    <p>Zmień wygląd zamówień online zgodnie z preferencjami.</p>
                    <p>Zapisz zmiany i przeloguj się na swoje konto.</p>
                    <p>Zmiany będą widoczne dopiero po ponownym zalogowaniu.</p>
                    <div className='Settings-container'>
                        <DefaultViewSettingWrapper
                            defaultView={defaultView}
                            isDesktop={isDesktop}
                            handleDefaultViewChange={handleDefaultViewChange}
                        />
                        <ButtonComponent
                            text='Zapisz'
                            onClick={handleSaveDefaultViewChange}
                            variant='primary'
                            margin='0.25rem'
                            height='37px'
                            onHoverAnimation={3}
                        />
                        {(isDefaultViewFetching || messageDefaultView !== '') ?
                            <div className={isDesktop ? 'Settings-message' : 'Settings-message-mobile'} style={{ color: messageDefaultView === 'Błąd' ? 'red' : 'green' }}>{isDefaultViewFetching ? <Spinner className='Settings-spinner-margin' animation='border' /> : messageDefaultView}</div> :
                            <div className={isDesktop ? 'Settings-placeholder' : 'Settings-placeholder-mobile'}></div>}
                    </div>
                </>}
            {permissionService.haveAccessToView('orders') &&
                <>
                    <h2>Zamówienia</h2>
                    <p>Ilość zamówień wyświetlanych na stronie</p>
                    <div className='Settings-container'>
                        <NumberOfOrdersPerPageSettingWrapper
                            initialValue={size}
                            changeHandler={handleSizeChange}
                            isDesktop={isDesktop}
                            id='pageSize'
                        />
                        <ButtonComponent
                            text='Zapisz'
                            onClick={handleSaveSizeChange}
                            variant='primary'
                            margin='0.25rem'
                            height='37px'
                            onHoverAnimation={3}
                        />
                        {(isSizeFetching || messageSize !== '') ?
                            <div className={isDesktop ? 'Settings-message' : 'Settings-message-mobile'} style={{ color: messageSize === 'Błąd' ? 'red' : 'green' }}>{isSizeFetching ? <Spinner className='Settings-spinner-margin' animation='border' /> : messageSize}</div> :
                            <div className={isDesktop ? 'Settings-placeholder' : 'Settings-placeholder-mobile'}></div>}
                    </div>
                </>}
            {permissionService.haveAccessToView('new-order') &&
                <>
                    <h2>Nowe zamówienie</h2>
                    <p>Domyślny widok (desktopowy)</p>
                    <div className='Settings-container'>
                        <DefaultOrdersViewSettingWrapper
                            isPlateCreatorOn={isPlateCreatorOn}
                            isDesktop={isDesktop}
                            initialValue={defaultDesktopView}
                            changeHandler={handleDesktopViewChange}
                        />
                        <ButtonComponent
                            text='Zapisz'
                            onClick={handleSaveDesktopViewChange}
                            variant='primary'
                            margin='0.25rem'
                            height='37px'
                            onHoverAnimation={3}
                        />
                        {(isDefaultDesktopViewFetching || messageDefaultDesktopView !== '') ?
                            <div className={isDesktop ? 'Settings-message' : 'Settings-message-mobile'} style={{ color: messageDefaultDesktopView === 'Błąd' ? 'red' : 'green' }}>{isDefaultDesktopViewFetching ? <Spinner className='Settings-spinner-margin' animation='border' /> : messageDefaultDesktopView}</div> :
                            <div className={isDesktop ? 'Settings-placeholder' : 'Settings-placeholder-mobile'}></div>}
                    </div>
                    <p>Domyślny widok (mobilny)</p>
                    <div className='Settings-container'>
                        <DefaultOrdersViewSettingWrapper
                            isPlateCreatorOn={isPlateCreatorOn}
                            isForMobileView
                            isDesktop={isDesktop}
                            initialValue={defaultMobileView}
                            changeHandler={handleMobileViewChange}
                        />
                        <ButtonComponent
                            text='Zapisz'
                            onClick={handleSaveMobileViewChange}
                            variant='primary'
                            margin='0.25rem'
                            height='37px'
                            onHoverAnimation={3}
                        />
                        {(isDefaultMobileViewFetching || messageDefaultMobileView !== '') ?
                            <div className={isDesktop ? 'Settings-message' : 'Settings-message-mobile'} style={{ color: messageDefaultMobileView === 'Błąd' ? 'red' : 'green' }}>{isDefaultMobileViewFetching ? <Spinner className='Settings-spinner-margin' animation='border' /> : messageDefaultMobileView}</div> :
                            <div className={isDesktop ? 'Settings-placeholder' : 'Settings-placeholder-mobile'}></div>}
                    </div>
                </>}
        </div>
    );
};

export default Settings;
